import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authCapabilitiesSchema } from '@bolao/shared';
import GoogleButton from '../components/GoogleButton.js';
import LanguageToggle from '../components/LanguageToggle.js';
import { authClient, useSession } from '../auth-client.js';
import logoUrl from '../assets/logo.png';
import styles from './AuthScreen.module.css';

async function fetchCapabilities() {
  const response = await fetch('/api/auth/capabilities');
  // A dev server with the API down answers this with the SPA's own
  // index.html rather than refusing the connection, so "did it 200 with
  // JSON" is the real check, not "did fetch reject".
  if (!response.ok) throw new Error(`Capabilities request failed: ${response.status}`);
  return authCapabilitiesSchema.parse(await response.json());
}

type Mode = 'signIn' | 'register';

/**
 * The conventional login screen.
 *
 * Email and password are rendered but disabled, with an explicit "coming soon"
 * badge. A screen offering only a Google button reads as half-built; showing the
 * planned method greyed out communicates the roadmap instead of hiding it.
 *
 * Only Google actually works today, and even that is reported by the API rather
 * than assumed — see `/api/auth/capabilities`.
 */
export default function AuthScreen() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('signIn');
  const { data: session, isPending: sessionPending } = useSession();

  const {
    data: capabilities,
    isError: capabilitiesUnreachable,
    isFetching: capabilitiesFetching,
    refetch: retryCapabilities,
  } = useQuery({
    queryKey: ['auth-capabilities'],
    queryFn: fetchCapabilities,
    retry: false,
  });

  const googleAvailable = capabilities?.google ?? false;

  if (sessionPending) {
    return (
      <main className={styles.screen}>
        <p className="muted" style={{ textAlign: 'center' }}>
          {t('auth.checkingSession')}
        </p>
      </main>
    );
  }

  if (session?.user) {
    return (
      <main className={styles.screen}>
        <div className={styles.topBar}>
          <LanguageToggle />
        </div>
        <div className={`panel ${styles.card} ${styles.signedIn}`}>
          {session.user.image ? (
            <img className={styles.avatar} src={session.user.image} alt="" />
          ) : null}
          <p>{t('auth.signedInAs', { name: session.user.name || session.user.email })}</p>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => void authClient.signOut()}
          >
            {t('auth.signOut')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.topBar}>
        <LanguageToggle />
      </div>

      <div className={styles.brand}>
        <img className={styles.logo} src={logoUrl} alt="" />
        <h1 className={styles.title}>{t('app.name')}</h1>
        <p className={styles.tagline}>{t('app.tagline')}</p>
      </div>

      <div className={`panel ${styles.card}`}>
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signIn'}
            className={`${styles.tab} ${mode === 'signIn' ? styles.tabActive : ''}`}
            onClick={() => setMode('signIn')}
          >
            {t('auth.signIn')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={`${styles.tab} ${mode === 'register' ? styles.tabActive : ''}`}
            onClick={() => setMode('register')}
          >
            {t('auth.createAccount')}
          </button>
        </div>

        <div className={styles.form}>
          <div className={styles.formHeader}>
            <span className="label" style={{ margin: 0 }}>
              {t('auth.withEmail')}
            </span>
            <span className={styles.comingSoon}>{t('auth.comingSoon')}</span>
          </div>

          <div className={styles.field}>
            <label className="label" htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              disabled
            />
          </div>

          <div className={styles.field}>
            <label className="label" htmlFor="password">
              {t('auth.password')}
            </label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
              placeholder={t('auth.passwordPlaceholder')}
              disabled
            />
          </div>

          <button type="button" className="button button-primary" disabled>
            {mode === 'signIn' ? t('auth.signIn') : t('auth.createAccount')}
          </button>
        </div>

        <div className={styles.divider}>{t('auth.or')}</div>

        <GoogleButton
          label={mode === 'signIn' ? t('auth.signInWithGoogle') : t('auth.signUpWithGoogle')}
          disabled={!googleAvailable}
          onClick={() =>
            void authClient.signIn.social({
              provider: 'google',
              callbackURL: window.location.origin,
            })
          }
        />

        {/* "Couldn't reach the server" and "Google isn't configured" are
            completely different problems with completely different fixes —
            reporting the second for the first sends someone hunting through
            OAuth config when the real answer is that the API isn't running. */}
        {capabilitiesUnreachable ? (
          <div className={styles.unreachable}>
            <p className="error">{t('auth.serverUnreachable')}</p>
            <button
              type="button"
              className="button button-secondary"
              disabled={capabilitiesFetching}
              onClick={() => void retryCapabilities()}
            >
              {capabilitiesFetching ? t('auth.retrying') : t('auth.retry')}
            </button>
          </div>
        ) : !googleAvailable ? (
          <p className="error">{t('auth.signInUnavailable')}</p>
        ) : null}

        <DevQuickSignIn />
      </div>

      <p className={styles.footer}>{t('auth.notABettingApp')}</p>
    </main>
  );
}

/**
 * DEV-ONLY: sign in as anybody, by name.
 *
 * Testing the things that actually matter here — joining a circle, filling a
 * bolão's roster, proving two bolões score independently — needs several
 * distinct people signed in at once. With Google as the only real method
 * that means a real Google account and a browser profile per tester, which
 * is enough friction that those paths were being skipped.
 *
 * `import.meta.env.DEV` is a compile-time constant, so this whole component
 * (and the fetch inside it) is dropped from a production bundle rather than
 * merely hidden. The route it calls is separately absent in production.
 *
 * Deliberately not translated: it never ships, and adding dev-only strings to
 * every locale file would be noise in the files that do.
 */
function DevQuickSignIn() {
  const [username, setUsername] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!import.meta.env.DEV) return null;

  const signIn = async () => {
    const name = username.trim();
    if (!name) return;

    setPending(true);
    setFailed(false);
    try {
      const response = await fetch('/api/dev/quick-account', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username: name }),
      });
      if (!response.ok) throw new Error(`Quick sign-in failed: ${response.status}`);
      // Full reload rather than refetching the session: every query in the
      // app is keyed to the old identity, and starting clean is the honest
      // way to become a different person.
      window.location.assign('/');
    } catch {
      setFailed(true);
      setPending(false);
    }
  };

  /* eslint-disable i18next/no-literal-string -- deliberately untranslated; see this component's doc comment. */
  return (
    <div className={styles.devPanel}>
      <span className={styles.devBadge}>Dev only</span>
      <p className={styles.devHint}>
        Sign in as anyone by name — reuse a name to come back as that person.
      </p>
      <div className={styles.devRow}>
        <input
          className="input"
          value={username}
          placeholder="alice"
          disabled={pending}
          onChange={(event) => setUsername(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void signIn();
          }}
        />
        <button
          type="button"
          className="button button-secondary"
          disabled={pending || username.trim().length === 0}
          onClick={() => void signIn()}
        >
          {pending ? '…' : 'Go'}
        </button>
      </div>
      {failed ? <p className="error">Quick sign-in failed — is the API running?</p> : null}
    </div>
  );
  /* eslint-enable i18next/no-literal-string */
}
